"use client"

import Link from "next/link"
import Image from "next/image"
import { motion, useReducedMotion } from "framer-motion"
import { formatArchiveRowDate } from "@/lib/format/date"
import { getPublicDemoEvent } from "@/lib/demo/public-demo-event"
import { getDisplayAddress } from "@/lib/location/address-model"
import { LANDING_MEMORY_STRIP } from "@/lib/marketing/landing-media"
import { eventCoverSrcThumbnail } from "@/lib/events/event-cover-display"
import { cn } from "@/lib/utils"
import {
  IMAGE_QUALITY_CARD_THUMB,
  IMAGE_QUALITY_MARKETING_GRID,
} from "@/lib/media/image-presets"

const easeOut = [0.22, 1, 0.36, 1] as const
const memoryTileLayout = [
  "col-span-12 sm:col-span-7 sm:row-span-2 aspect-[5/4] sm:aspect-auto",
  "col-span-6 sm:col-span-5 aspect-[4/5]",
  "col-span-6 sm:col-span-5 aspect-[4/5]",
  "col-span-7 sm:col-span-5 aspect-[5/4]",
  "col-span-5 sm:col-span-3 aspect-square",
  "col-span-12 sm:col-span-4 aspect-[16/9]",
] as const

export function MarketingMemoryPreview() {
  const reduceMotion = useReducedMotion()
  const demo = getPublicDemoEvent()

  return (
    <section className="relative w-full min-w-0 py-36 sm:py-48">
      <div className="mx-auto grid w-full min-w-0 max-w-[73rem] gap-16 px-4 sm:px-0 lg:grid-cols-12 lg:items-start lg:gap-16">
        <div className="lg:col-span-7">
          <motion.div
            className="relative"
            initial={reduceMotion ? false : { opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-40px" }}
            transition={{
              duration: reduceMotion ? 0 : 0.55,
              delay: reduceMotion ? 0 : 0.06,
              ease: easeOut,
            }}
          >
            <p className="type-marketing-eyebrow mb-3 text-left">On your timeline</p>
            <Link href="/demo" className="group relative block w-full min-w-0 max-w-full">
              <div
                className={cn(
                  "marketing-interactive-card relative w-full min-w-0 max-w-full overflow-hidden p-4 backdrop-blur-sm sm:p-6",
                  "hover:border-border/85 hover:bg-card/78",
                )}
              >
                <div className="flex w-full min-w-0 max-w-full gap-4 sm:gap-6">
                  <div className="relative h-24 w-24 shrink-0 overflow-hidden rounded-xl ring-1 ring-border/45 sm:h-32 sm:w-32">
                    <Image
                      src={eventCoverSrcThumbnail(demo)}
                      alt={demo.title}
                      fill
                      sizes="(max-width: 640px) 6rem, 8rem"
                      quality={IMAGE_QUALITY_CARD_THUMB}
                      className="object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="mb-2 flex min-w-0 items-start justify-between gap-2 sm:gap-4">
                      <div className="min-w-0">
                        <h3 className="type-card-title mb-1 line-clamp-2 font-semibold sm:line-clamp-1">{demo.title}</h3>
                        <span className="marketing-interactive-chip type-caption inline-flex px-2 py-0.5 text-foreground">
                          Hosted
                        </span>
                      </div>
                    </div>
                    <div className="type-body-sm mb-2 flex w-full min-w-0 flex-wrap items-center gap-x-3 gap-y-1 text-muted-foreground">
                      <span>{formatArchiveRowDate(demo.date)}</span>
                      <span className="flex min-w-0 items-center">
                        <span className="max-w-full break-words sm:truncate sm:max-w-[var(--max-width-location-md)]">
                          {getDisplayAddress(demo, "single")}
                        </span>
                      </span>
                    </div>
                    <div className="type-caption">{demo.attendees} people marked the date</div>
                  </div>
                </div>
              </div>
            </Link>
          </motion.div>
        </div>

        <div className="text-left lg:col-span-5 lg:pt-5">
          <motion.div
            className="mb-3 text-muted-foreground"
            initial={reduceMotion ? false : { opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: reduceMotion ? 0 : 0.5, ease: easeOut }}
          >
            <span className="type-marketing-eyebrow">After the evening</span>
          </motion.div>
          <motion.h2
            className="type-display-title mb-5 max-w-[15ch] text-balance text-[clamp(2rem,4.6vw,3.5rem)] sm:mb-6"
            initial={reduceMotion ? false : { opacity: 0, y: 14 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{
              duration: reduceMotion ? 0 : 0.55,
              delay: reduceMotion ? 0 : 0.04,
              ease: easeOut,
            }}
          >
            A gathering ends. The memory does not.
          </motion.h2>
          <motion.p
            className="type-hero-subtitle max-w-[36ch] text-foreground/78"
            initial={reduceMotion ? false : { opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{
              duration: reduceMotion ? 0 : 0.5,
              delay: reduceMotion ? 0 : 0.08,
              ease: easeOut,
            }}
          >
            Everything shared stays with the people it belongs to.
          </motion.p>
        </div>
      </div>

      <div className="relative mx-auto mt-20 max-w-[72rem] sm:mt-28">
        <motion.div
          className="mx-auto max-w-[min(100%,68rem)]"
          initial={reduceMotion ? false : { opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-40px" }}
          transition={{
            duration: reduceMotion ? 0 : 0.5,
            delay: reduceMotion ? 0 : 0.1,
            ease: easeOut,
          }}
          aria-label="Fragments from the same evening"
        >
          <div className="grid grid-cols-12 auto-rows-[7.2rem] gap-2 sm:auto-rows-[8.4rem] sm:gap-3">
            {LANDING_MEMORY_STRIP.map((photo, i) => (
              <div
                key={`landing-memory-grid-${i}`}
                className={cn(
                  "group relative overflow-hidden rounded-xl sm:rounded-2xl",
                  memoryTileLayout[i % memoryTileLayout.length],
                )}
              >
                <Image
                  src={photo.src}
                  alt={photo.alt}
                  fill
                  sizes="(max-width: 640px) 45vw, (max-width: 1024px) 28vw, 18vw"
                  quality={IMAGE_QUALITY_MARKETING_GRID}
                  className="object-cover transition-transform duration-700 ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-[1.03]"
                  loading="lazy"
                />
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  )
}
